'use client'

import { useTransition } from 'react'
import { useRouter } from 'next/navigation'

type Locale = 'nl' | 'en'

type Props = {
  current: Locale
  disabled?: boolean
  disabledTitle?: string
  labelNl: string
  labelEn: string
}

/**
 * Locale-dropdown voor de top-bar.
 *
 * - Zet altijd een `locale`-cookie zodat next-intl het pre-login én
 *   post-login direct kan oppikken (bron-volgorde in i18n.ts:
 *   JWT-claim > cookie > Accept-Language > nl).
 * - Doet daarnaast best-effort PATCH /api/klant/me/locale zodat de
 *   keuze ingelogde users persistent in de DB staat. Pre-login geeft
 *   die endpoint 401 — niet erg, de cookie heeft dan al gewerkt.
 * - Tijdens impersonation is de switch disabled (de API blokkeert het
 *   toch al met 403, maar we tonen het visueel ook).
 */
export function LocaleSwitcher({ current, disabled, disabledTitle, labelNl, labelEn }: Props) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()

  function onChange(next: Locale) {
    if (disabled || isPending || next === current) return
    startTransition(async () => {
      document.cookie = `locale=${next}; path=/; max-age=31536000; samesite=lax`
      try {
        await fetch('/api/klant/me/locale', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale: next }),
        })
      } catch {
        // Pre-login: 401 is verwacht — cookie heeft de switch al doorgevoerd.
      }
      router.refresh()
    })
  }

  return (
    <div
      className="inline-flex items-center overflow-hidden rounded-lg border border-[#CED0C8] bg-white text-xs"
      title={disabled ? disabledTitle : undefined}
      aria-disabled={disabled}
    >
      <button
        type="button"
        onClick={() => onChange('nl')}
        disabled={disabled || isPending}
        className={
          'px-2.5 py-1.5 transition ' +
          (current === 'nl'
            ? 'bg-[#1A1A18] text-white'
            : 'text-[#1A1A18] hover:bg-[#F0F1EC] disabled:opacity-50')
        }
      >
        {labelNl}
      </button>
      <button
        type="button"
        onClick={() => onChange('en')}
        disabled={disabled || isPending}
        className={
          'px-2.5 py-1.5 transition ' +
          (current === 'en'
            ? 'bg-[#1A1A18] text-white'
            : 'text-[#1A1A18] hover:bg-[#F0F1EC] disabled:opacity-50')
        }
      >
        {labelEn}
      </button>
    </div>
  )
}
